import { useEffect, useState, useContext } from "react";
import { getInlineLoader } from "../Helpers";
import banner1 from "../assets/banner1.png";
import banner2 from "../assets/banner2.png";
import banner3 from "../assets/banner3.png";
import banner4 from "../assets/banner4.png";
import { AppContext } from "../App";
import CallToAction from "./callToAction";
import {
  FaArrowRight,
  FaBolt,
  FaBuilding,
  FaCheckCircle,
  FaCog,
  FaLeaf,
  FaShieldAlt,
  FaSolarPanel,
  FaTools,
  FaUsers,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import { IoMdArrowForward } from "react-icons/io";

const services = [
  {
    id: 1,
    slug: "electrical-engineering",
    badge: "Power Systems",
    icon: <FaBolt className="fs-1" />,
    gradient: "from-amber-400 to-orange-500",
    title: "Electrical Engineering",
    shortDescription: "Complete LV/HV power solutions from design to commissioning.",
    description:
      "We deliver end-to-end electrical engineering services including high and low voltage installations, transformer setups, switchgear, testing, and maintenance. Our certified team ensures compliance with TANESCO standards and international safety protocols.",
    features: [
      "LV & HV Installation & Maintenance",
      "Transformer Supply & Commissioning",
      "33kV/11kV Power Line Construction",
      "Testing, Metering & Protection Systems",
    ],
    image: banner1,
  },
  {
    id: 2,
    slug: "civil-engineering",
    badge: "Infrastructure",
    icon: <FaBuilding className="fs-1" />,
    gradient: "from-blue-500 to-cyan-400",
    title: "Civil Engineering",
    shortDescription: "Robust infrastructure built to last with precision engineering.",
    description:
      "Our civil engineering division handles structural works for electrical substations, transmission tower foundations, road and bridge construction, and gabion soil protection systems. We combine engineering precision with local terrain expertise.",
    features: [
      "Sub-Station Foundation Works",
      "Transmission Tower & Pole Installation",
      "Roads, Bridges & Drainage Systems",
      "Gabions & Soil Erosion Control",
    ],
    image: banner2,
  },
  {
    id: 3,
    slug: "renewable-energy",
    badge: "Sustainability",
    icon: <FaSolarPanel className="fs-1" />,
    gradient: "from-emerald-400 to-teal-500",
    title: "Renewable Energy",
    shortDescription: "Sustainable power solutions for a greener, cost-effective future.",
    description:
      "We design and install solar PV systems, hybrid energy solutions, and renewable energy structures for commercial, industrial, and community applications. Reduce operational costs and carbon footprint with our expert-led green energy projects.",
    features: [
      "Solar PV System Design & Installation",
      "Hybrid Solar-Diesel Solutions",
      "Energy Audits & Feasibility Studies",
      "Battery Storage & Inverter Integration",
    ],
    image: banner3,
  },
  {
    id: 4,
    slug: "project-consultancy",
    badge: "Expertise",
    icon: <FaCog className="fs-1" />,
    gradient: "from-violet-500 to-purple-400",
    title: "Project Management & Consultancy",
    shortDescription: "Strategic oversight from concept to successful completion.",
    description:
      "Our consultancy arm provides technical advisory, project planning, procurement support, root cause analysis, and capacity building. We help clients navigate complex engineering challenges with data-driven strategies and proven methodologies.",
    features: [
      "End-to-End Project Management",
      "Technical Feasibility & Design Review",
      "Procurement & Supply Chain Advisory",
      "Training & Knowledge Transfer",
    ],
    image: banner4,
  },
];

const trustPoints = [
  {
    icon: <FaShieldAlt className="fs-3" />,
    title: "Fully Licensed",
    desc: "CRB Electrical & Civil, TANESCO Contractor, VAT/TIN Registered",
  },
  {
    icon: <FaUsers className="fs-3" />,
    title: "Expert Team",
    desc: "7+ certified engineers with 8-10 years average experience",
  },
  {
    icon: <FaCheckCircle className="fs-3" />,
    title: "Quality Assured",
    desc: "ISO-aligned processes with rigorous safety and testing protocols",
  },
];

export default function Services() {
  const [ready, setReady] = useState(false);
  const appContext = useContext(AppContext);

  useEffect(() => {
    AOS.init({ once: true, offset: 100, easing: "ease-out", duration: 600 });
    setReady(true);
  }, []);

  if (!ready) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center">{getInlineLoader()}</div>
      </div>
    );
  }

  return (
    <>
      {/* Modern Hero Section */}
      <section
        className="position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)",
          paddingTop: "140px",
          paddingBottom: "100px",
        }}
      >
        

        <div className="container position-relative px-3">
          <div className="row align-items-center g-5">
            <div className="col-lg-7" data-aos="fade-right">
              <h1 className="display-4 fw-bold text-white mb-4" style={{ lineHeight: 1.1 }}>
                Powering Tanzania's Infrastructure{" "}
                <span className="text-primary">With Precision</span>
              </h1>
              <p className="lead text-white-50 mb-5" style={{ maxWidth: "580px", lineHeight: 1.7 }}>
                From high-voltage installations to renewable energy systems, we deliver integrated engineering solutions that drive growth, reliability, and sustainability across East Africa.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <button
                  className="btn btn-primary btn-lg px-5 py-3 rounded fw-semibold"
                  style={{ backgroundColor: "#3B82F6", border: "none" }}
                  onClick={() =>
                    document.getElementById("services-grid")?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Explore Services
                </button>
                <button className="btn btn-outline-light btn-lg px-5 py-3 rounded fw-semibold">
                  Request Consultation
                </button>
              </div>
            </div>

            {/* Hero Visual Card */}
            <div className="col-lg-5" data-aos="fade-left">
              <div className="position-relative">
                <div
                  className="rounded-4 overflow-hidden shadow-2xl"
                  style={{
                    border: "3px solid rgba(255,255,255,0.15)",
                    boxShadow: "0 25px 80px rgba(0,0,0,0.4)",
                  }}
                >
                  <img
                    src={banner2}
                    alt="Engineering project site"
                    className="img-fluid w-100"
                    style={{ objectFit: "cover", height: "420px" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="position-absolute bottom-0 start-0 w-100">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-100">
            <path
              d="M0 32L60 26.7C120 21 240 11 360 10.7C480 11 600 21 720 26.7C840 32 960 32 1080 26.7C1200 21 1320 11 1380 5.3L1440 0V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0V32Z"
              fill="white"
              fillOpacity="0.05"
            />
          </svg>
        </div>
      </section>

      {/* Trust Indicators Bar */}
      <section className="py-4 bg-white border-bottom">
        <div className="container px-3">
          <div className="row g-4 justify-content-center">
            {trustPoints.map((item, index) => (
              <div key={item.title} className="col-6 col-md-4" data-aos="fade-up" data-aos-delay={index * 80}>
                <div className="text-center">
                  <div className="text-primary mb-2">{item.icon}</div>
                  <h6 className="fw-bold mb-1">{item.title}</h6>
                  <small className="text-muted d-block">{item.desc}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section id="services-grid" className="MainBodySection py-5 my-5">
        <div className="container px-3">
          {/* Section Header */}
          <div className="text-center mb-5" data-aos="fade-up">
            <span className="badge bg-primary bg-opacity-10 text-primary px-4 py-2 rounded mb-3 fs-3">
              Our Expertise
            </span>
            <h2 className="display-5 font-bold mb-3 text-primary">Comprehensive Engineering Solutions</h2>
            <p className="lead text-muted font-semi-bold" style={{ maxWidth: "650px", margin: "0 auto" }}>
              We combine technical excellence with local insight to deliver infrastructure that powers progress.
            </p>
          </div>

          {/* Services Cards - Clean 2x2 Grid */}
          <div className="row g-4">
            {services.map((service, index) => (
              <div key={service.id} className="col-lg-6 mt-4" data-aos="fade-up" data-aos-delay={index * 100}>
                <div
                  className="card border-0 h-100 position-relative overflow-hidden"
                  style={{
                    borderRadius: "24px",
                    boxShadow: "0 5px 10px rgba(0,0,0,0.08)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    cursor: "pointer"
                  }}
                  onClick={() => appContext.navTo({ item: "services", subItem: service.slug })}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = "0 20px 60px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 10px 40px rgba(0,0,0,0.08)";
                  }}
                >
                  {/* Gradient Accent Header */}
                  <div className={`card-header bg-primary ${service.gradient} border-0 p-4`}>
                    <div className="d-flex align-items-start text-white">
                      <div className="bg-white bg-opacity-20 p-3 rounded-3 me-3 text-primary flex-shrink-0">
                        {service.icon}
                      </div>
                      <div>
                        <span className="badge bg-white bg-opacity-20 text-white px-3 py-2 rounded mb-2 text-muted">
                          {service.badge}
                        </span>
                        <h4 className="fw-bold mb-0">{service.title}</h4>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="card-body d-flex flex-column justify-content-between p-4">

                    {/* Service Image Preview */}
                    <div className="mb-4">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="img-fluid rounded-3"
                        style={{ height: "180px", objectFit: "cover", width: "100%" }}
                      />
                    </div>

                    <p className="text-muted mb-4 font-semi-bold">{service.shortDescription}</p>
                    

                    {/* Features List */}
                    <div className="row g-2 mb-4">
                      {service.features.map((feature, i) => (
                        <div key={i} className="col-12">
                          <div className="d-flex align-items-center">
                            <FaCheckCircle className="text-success me-2 flex-shrink-0" size={14} />
                            <small className="text-muted">{feature}</small>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="">
                      <button className="btn btn-primary">
                        Learn More <IoMdArrowForward size={20} />
                      </button>
                    </div>

                    

                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose MGE - Clean Split Layout */}
      <section className="MainBodySection py-5" style={{ background: "#f8fafc" }}>
        <div className="container px-3">
          <div className="row g-5 align-items-center">
            <div className="col-lg-5" data-aos="fade-right">
              <h2 className="display-6 fw-bold mb-4">
                Why Partner with <span className="text-primary">Medal Global Engineering</span>
              </h2>
              <p className="lead text-muted mb-4">
                Medal Global Engineering combines certified expertise, rigorous safety standards, and local knowledge to deliver infrastructure that lasts.
              </p>
              <ul className="list-unstyled mb-0">
                {[
                  "CRB & TANESCO licensed contractor",
                  "End-to-end project ownership",
                  "Transparent pricing & timelines",
                  "Post-completion support & maintenance",
                ].map((item, i) => (
                  <li key={i} className="d-flex align-items-start mb-3">
                    <FaCheckCircle className="text-success mt-1 me-3" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-lg-7">
              <div className="row g-4">
                {[
                  {
                    img: banner1,
                    title: "Precision Execution",
                    desc: "Every installation follows strict technical specs and safety protocols",
                  },
                  {
                    img: banner3,
                    title: "Local Expertise",
                    desc: "Deep understanding of Tanzania's terrain, regulations, and supply chains",
                  },
                ].map((item, i) => (
                  <div key={i} className="col-md-6" data-aos="fade-up" data-aos-delay={i * 100}>
                    <div className="card border-0 h-100" style={{ borderRadius: "20px", overflow: "hidden" }}>
                      <img
                        src={item.img}
                        alt={item.title}
                        className="img-fluid"
                        style={{ height: "200px", objectFit: "cover" }}
                      />
                      <div className="card-body p-4">
                        <h5 className="fw-bold mb-2">{item.title}</h5>
                        <p className="text-muted mb-0 small">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}